import WixKnowledgePreferences from './WixKnowledgePreferences';
import { startWixConnect } from '../utils/wixConnect';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Switch, FormControlLabel } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faArrowLeft, faXmark, faRotate, faSpinner, faMagnifyingGlass,
  faBox, faCalendarCheck, faTicket, faIdCard, faUtensils, faReceipt, faUserPlus,
  faLayerGroup, faBolt, faTriangleExclamation, faSliders, faDatabase, faChevronLeft, faChevronRight, faChevronDown, faToggleOn, faToggleOff,
  faBook, faCheck, faEye, faLock, faNewspaper, faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import Integrations from './Integrations.js';
import KnowledgeModal from './knowledgeModal.js';
import ProductsPreviewList from './ProductsPreviewList.js';
import ProductSyncSettingsModal from './ProductSyncSettingsModal.js';
import { guardWrite } from '../utils/permissions';
import './knowledgeOverview.css';   // shared .knowledge-search-* (matches Website Links)
import './ProductsPreviewModal.css';   // shared .pp-* list + pagination (matches Products)
import './ProviderHub.css';

const API = process.env.REACT_APP_API_BASE_URL;

// Map a backend dimension `icon` NAME → an actual icon asset. This is the ONLY
// frontend coupling: a new dimension declared in the catalog renders on its own,
// and an unknown icon name simply falls back to a neutral glyph — so the backend
// catalog stays the single source of truth.
const DIM_ICONS = {
  box: faBox,
  'calendar-check': faCalendarCheck,
  ticket: faTicket,
  'id-card': faIdCard,
  utensils: faUtensils,
  receipt: faReceipt,
  'user-plus': faUserPlus,
  database: faDatabase,
  book: faBook,
  newspaper: faNewspaper,
  building: faBuilding,
};
const dimIcon = (name) => DIM_ICONS[name] || faLayerGroup;
// Wix's own logos for its knowledge sources (Robert, 17.10.2026): products come from Wix Stores
const WIX_DIM_LOGOS = { products: '/wix/stores.png', site_content: '/wix-logo.png' };

function DimMark({ provider, d, className }) {
  const logo = provider === 'wix' ? WIX_DIM_LOGOS[d.key] : null;
  return logo
    ? <span className={`${className} has-logo${d.key === 'site_content' ? ' is-wordmark' : ''}`}><img src={logo} alt="" /></span>
    : <span className={className}><FontAwesomeIcon icon={dimIcon(d.icon)} /></span>;
}

// Access modes whose data is HELD by us (mirrored into the agent and curatable)
// as opposed to fetched live at answer time. Drives the overview's two zones and
// which drill-in panel a dimension opens.
const STORED_MODES = new Set(['mirrored', 'collections', 'store_content']);

function fmtWhen(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return null;
  }
}

function fmtCell(v) {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// The provider's owner-facing hub, rendered inside the Apps modal. It fetches the
// provider's `dimensions` from the backend and renders them generically:
//   • overview  → one card per dimension (mirrored: a count; live: a "Live" pill;
//                 visitor-bound: "Used by skills")
//   • drill-in  → mirrored opens the product sync panel; live fetches a preview
// If a provider declares no dimensions (or isn't connected) it falls back to the
// existing connect / manage detail, so nothing regresses for other apps.
// `variant`: 'modal' is the Apps modal it was born in (height pinning, morph
// animation, connection footer); 'page' is the Knowledge Base leaf, where the hub
// IS the page — no modal to measure, no scroll container to wrestle, and the
// connection is managed over in Apps.
export default function ProviderHub({ provider, app, botId, initialDimension, variant = 'modal' }) {
  const isPage = variant === 'page';
  const { t } = useTranslation();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);   // null = overview, else a dimension key

  // Keep the modal from collapsing when you open a section: on drill-in we pin the
  // container to the height it had (so the spinner sits centered in the same box),
  // then release once the section's data has loaded so it animates to its real size.
  const rootRef = useRef(null);
  const [pinnedHeight, setPinnedHeight] = useState(null);
  const [detailReady, setDetailReady] = useState(false);

  // The modal body (max-height + overflow-y:auto) is the real scroller; the overlay
  // can scroll too. Snap both to the top so a drilled-in section always starts at
  // the top, not wherever the overview was scrolled.
  const snapToTop = () => {
    const el = rootRef.current;
    if (!el) return;
    const body = el.closest('.channel-modal-body');
    if (body) body.scrollTop = 0;
    const overlay = el.closest('.channel-modal-overlay');
    if (overlay) overlay.scrollTop = 0;
  };

  const openDimension = (key) => {
    // On the page there is no modal box to keep from collapsing: just swap the
    // view and put the reader back at the top of the document.
    if (isPage) {
      setDetailReady(false);
      setView(key);
      window.scrollTo({ top: 0 });
      return;
    }
    const el = rootRef.current;
    if (el) {
      const body = el.closest('.channel-modal-body');
      if (body) {
        // Hold the current height, but cap it to the body's VISIBLE height so the
        // modal never grows a scrollbar mid-load and the centered spinner stays in
        // view (40 = the body's 20px padding x2).
        const visible = body.clientHeight - 40;
        setPinnedHeight(Math.max(200, Math.min(el.offsetHeight, visible || el.offsetHeight)));
        // Kill the scrollbar instantly, before the height shrink starts, so it
        // doesn't flash during the 0.32s morph animation.
        body.style.overflowY = 'hidden';
      } else {
        setPinnedHeight(el.offsetHeight);
      }
    }
    snapToTop();
    setDetailReady(false);
    setView(key);
  };
  const backToOverview = () => { setView(null); setDetailReady(false); };

  // After the section actually changes, re-assert the top (the open handler snaps
  // pre-render; this catches the post-render layout).
  useEffect(() => { snapToTop(); }, [view]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the body scrollbar suppressed while the modal animates into a section and
  // the content loads, then restore it just after the 0.32s morph settles — so the
  // real scrollbar appears once at the end instead of flashing in and out.
  useEffect(() => {
    const el = rootRef.current;
    const body = el && el.closest('.channel-modal-body');
    if (!body) return undefined;
    if (!view) { body.style.overflowY = ''; return undefined; }
    body.style.overflowY = 'hidden';
    if (!detailReady) return undefined;
    const id = setTimeout(() => { body.style.overflowY = ''; }, 380);   // > morph 0.32s
    return () => clearTimeout(id);
  }, [view, detailReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadHub = useCallback(async () => {
    if (!provider || !botId) { setHub(null); setLoading(false); return null; }
    try {
      const res = await fetch(
        `${API}apps/${encodeURIComponent(provider)}/dimensions?bot_id=${encodeURIComponent(botId)}`,
      );
      if (res.ok) {
        const data = await res.json();
        setHub(data);
        return data;
      }
    } catch {
      /* keep previous hub; the modal just won't refresh */
    } finally {
      setLoading(false);
    }
    return null;
  }, [provider, botId]);

  useEffect(() => { setLoading(true); loadHub(); }, [loadHub]);

  // Live mirrored-catalog status over the SAME {knowledge_id, status} WebSocket the
  // knowledge base uses — so a product sync started here updates the count/pill
  // without polling.
  const hubRef = useRef(null);
  useEffect(() => { hubRef.current = hub; }, [hub]);
  useEffect(() => {
    if (!botId) return undefined;
    let socket;
    let reconnectTimer = null;
    const connect = () => {
      socket = new WebSocket(process.env.REACT_APP_API_WEBSOCKET_URL);
      socket.onopen = () => socket.send(JSON.stringify({
        type: 'init', user_id: localStorage.getItem('user_id'), token: localStorage.getItem('token'),
      }));
      socket.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        if (!msg || !msg.knowledge_id || !msg.status) return;
        const dims = hubRef.current && hubRef.current.dimensions;
        if (!dims) return;
        const dim = dims.find((d) => d.knowledge_id && d.knowledge_id === msg.knowledge_id);
        if (!dim) return;
        // 'processing' messages carry live { done, total } for an "X / Y" indicator;
        // carry them forward (and keep the last seen if a message omits them).
        setHub((prev) => (prev && prev.dimensions)
          ? { ...prev, dimensions: prev.dimensions.map((d) => (d.key === dim.key
              ? { ...d, status: msg.status,
                  syncDone: typeof msg.done === 'number' ? msg.done : d.syncDone,
                  syncTotal: typeof msg.total === 'number' ? msg.total : d.syncTotal }
              : d)) }
          : prev);
        // A terminal status refetches the hub (real counts) — which also drops the
        // transient syncDone/syncTotal, so the card returns to its synced count.
        if (msg.status === 'completed' || msg.status === 'failed') loadHub();
      };
      socket.onclose = (evt) => {
        if (evt && evt.code === 4401) return;   // auth failure: no reconnect loop
        reconnectTimer = setTimeout(connect, 5000);
      };
    };
    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) { socket.onclose = null; try { socket.close(); } catch {} }
    };
  }, [botId, loadHub]);

  // Deep-link auto-drill: opened with an initialDimension (the Apps deep-link from
  // the skill builder's "View products" link) opens that dimension once the hub has
  // loaded and the dimension exists + is openable. Applied once.
  const appliedInitialRef = useRef(false);
  useEffect(() => {
    if (appliedInitialRef.current || !initialDimension || !hub || !hub.connected) return;
    const dim = (hub.dimensions || []).find((d) => d.key === initialDimension);
    if (!dim || (provider === 'wix' && (isPage
      ? !['products', 'site_content'].includes(dim.key)
      : STORED_MODES.has(dim.access_mode)))) return;
    // Every stored mode is openable, plus any live dimension with an owner view.
    // (This used to list 'mirrored' only, so a deep link into Site content or
    // Policies & pages silently landed on the overview.)
    const clickable = STORED_MODES.has(dim.access_mode) || (dim.access_mode === 'live' && dim.owner_preview);
    if (!clickable) return;
    appliedInitialRef.current = true;
    setView(initialDimension);
  }, [hub, initialDimension, provider, isPage]);

  const dims = ((hub && hub.dimensions) || [])
    .filter(d => provider !== 'wix' || (isPage
      ? ['products', 'site_content'].includes(d.key)
      : !STORED_MODES.has(d.access_mode)))
    .map(d => provider === 'wix' ? { ...d,
      label: t(`wix_area_${d.key}`, d.label),
      summary: t(`wix_area_${d.key}_sub`, d.summary || ''),
    } : d);

  if (loading) {
    return <div className="phub-loading"><FontAwesomeIcon icon={faSpinner} spin /></div>;
  }
  // Wix: the backend reports the precise install state. An old / uninstalled app
  // ('reinstall') or an install missing the new permissions ('needs_scopes') must
  // NOT show the data dimensions as if they work — show the matching prompt instead,
  // with a button into the Wix App Market installer (re-consent / reinstall flow).
  if (hub && (hub.wix_state === 'reinstall' || hub.wix_state === 'needs_scopes')) {
    return <WixConnectionPrompt state={hub.wix_state} missing={hub.missing_scopes} botId={botId} t={t} />;
  }
  // Shopify: connected implicitly via the App-Store install — there is no connect
  // flow here. Not bound (or token gone after an uninstall) shows the matching
  // instruction instead of the generic OAuth connect detail.
  if (provider === 'shopify' && hub && (hub.shopify_state === 'not_connected' || hub.shopify_state === 'no_token')) {
    return <ShopifyConnectionPrompt state={hub.shopify_state} t={t} />;
  }
  // WooCommerce: connected through the Ultimo Bots WordPress plugin on the agent's site — nothing to connect here.
  // The backend says what is missing (no site, WooCommerce off, plugin too old).
  if (provider === 'woocommerce' && hub && hub.woocommerce_state && hub.woocommerce_state !== 'ok') {
    return <WooConnectionPrompt state={hub.woocommerce_state} site={hub.woocommerce_site} t={t} />;
  }
  // Backend-driven: a provider gets the hub only when it declares dimensions AND
  // is connected. Otherwise show the existing connect / manage flow.
  if (!hub || !hub.connected || dims.length === 0) {
    return <Integrations variant="detail" onlyProvider={provider} seedProvider={app} botId={botId} />;
  }

  const activeDim = view ? dims.find((d) => d.key === view) : null;

  if (activeDim) {
    // While the section loads, hold the previous height and show a centered
    // spinner; keep the panel mounted (so it fetches) but hidden until it's ready.
    return (
      <div
        className={`phub${isPage ? ' phub-page' : ''}`}
        ref={rootRef}
        style={!detailReady && pinnedHeight ? { minHeight: pinnedHeight } : undefined}
      >
        <button type="button" className="phub-back" onClick={backToOverview}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t('phub_back', 'Back')}
        </button>
        <div className="phub-detail-head">
          <DimMark provider={provider} d={activeDim} className="phub-detail-icon" />
          <div className="phub-detail-heads">
            <h3 className="phub-detail-title">{activeDim.label}</h3>
            {activeDim.summary && <p className="phub-detail-sub">{activeDim.summary}</p>}
          </div>
        </div>
        {activeDim.owner_only && <p className="phub-row-note"><FontAwesomeIcon icon={faLock} />{t('phub_owner_only')}</p>}
        {!detailReady && (
          <div className="phub-detail-spinner"><FontAwesomeIcon icon={faSpinner} spin /></div>
        )}
        {/* The panels below were written for the Apps modal, whose body is already
            white, so most of their containers carry no background of their own. On
            the page they sit on the grey section canvas, where that shows through
            as grey tables and lists. One white surface here covers every panel,
            including any added later. */}
        <div
          className="phub-detail-body"
          style={!detailReady ? { display: 'none' } : undefined}
        >
          {activeDim.access_mode === 'mirrored'
            ? <MirroredPanel provider={provider} botId={botId} dim={activeDim} refreshHub={loadHub} onReady={() => setDetailReady(true)} />
            : activeDim.access_mode === 'collections'
              ? <CollectionsPanel botId={botId} dim={activeDim} onReady={() => setDetailReady(true)} />
              : activeDim.access_mode === 'store_content'
                ? <ShopifyContentPanel botId={botId} onReady={() => setDetailReady(true)} />
                : <LivePreviewPanel provider={provider} botId={botId} dim={activeDim} onReady={() => setDetailReady(true)} />}
        </div>
      </div>
    );
  }

  // The overview splits the provider's dimensions along the line that actually
  // matters to an owner: what we KEEP (mirrored into the agent, curatable, has a
  // real count and a freshness stamp) versus what we READ at answer time (nothing
  // stored, nothing to curate). One flat grid hid that distinction behind ten
  // identically-weighted cards and a "Live" pill that appeared so often it stopped
  // carrying information.
  const stored = dims.filter((d) => STORED_MODES.has(d.access_mode));
  const live = dims.filter((d) => !STORED_MODES.has(d.access_mode));

  if (provider === 'wix' && isPage) return (
    <div className="phub phub-page phub-wix-knowledge" ref={rootRef}>
      <WixKnowledgePreferences key={botId} botId={botId}>
        {({ value, busy, toggle }) => <div className="phub-tiles">
          {stored.map(d => {
            const source = d.key === 'products' ? 'products' : 'cms';
            return <article className="wix-source-card" key={d.key}>
              <StoredTile d={d} showDescription provider={provider} onOpen={() => openDimension(d.key)} t={t} />
              <div className="wix-source-control">
                <span>{t('wix_use_in_answers')}</span>
                <Switch size="small" checked={Boolean(value?.[`${source}_enabled`])}
                  disabled={!value || busy} color="secondary"
                  inputProps={{ 'aria-label': t(source === 'products' ? 'skill_flow_wix_products_label' : 'skill_flow_wix_cms_label') }}
                  onChange={(_, enabled) => toggle(`${source}_enabled`, enabled)} />
              </div>
            </article>;
          })}
        </div>}
      </WixKnowledgePreferences>
    </div>
  );

  return (
    <div className={`phub${isPage ? ' phub-page' : ''}${provider === 'wix' ? ' phub-wix-capabilities' : ''}`} ref={rootRef}>
      {!isPage && provider !== 'wix' && (
        <p className="phub-intro">
          {t('phub_intro', 'What {{label}} gives your assistant. Open an area to see its data.', { label: hub.label })}
        </p>
      )}

      {stored.length > 0 && (
        <section className="phub-zone">
          <header className="phub-zone-head">
            <h3 className="phub-zone-title">{t('phub_zone_stored_title', 'Kept in your assistant')}</h3>
            <p className="phub-zone-sub">
              {t('phub_zone_stored_sub', 'Mirrored from {{label}} so your assistant can search it instantly. You decide what is included.', { label: hub.label })}
            </p>
          </header>
          <div className="phub-tiles">
            {stored.map((d) => (
              <StoredTile key={d.key} d={d} onOpen={() => openDimension(d.key)} t={t} />
            ))}
          </div>
        </section>
      )}

      {live.length > 0 && (
        <section className="phub-zone">
          <header className="phub-zone-head">
            <h3 className="phub-zone-title">{provider === 'wix' ? t('wix_explore_capabilities') : t('phub_zone_live_title', 'Read live from {{label}}', { label: hub.label })}</h3>
            <p className="phub-zone-sub">
              {provider === 'wix' ? t('wix_capabilities_hint') : t('phub_zone_live_sub', 'Nothing is stored here. Your assistant fetches these the moment a visitor asks, so the answer is never out of date.')}
            </p>
          </header>
          <div className="phub-rows">
            {live.map((d) => (
              <LiveRow
                key={d.key}
                d={d}
                onOpen={d.owner_preview ? () => openDimension(d.key) : null}
                t={t}
              />
            ))}
          </div>
        </section>
      )}

      {/* "About this app" (can/cannot prose) belongs on the connect screen only;
          once the app is connected the dimensions lead, so it's omitted here.
          There is no connection footer either: for Wix and Shopify the hub only
          ever renders for an UNBOUND agent now (AppsPage hands a connected one to
          its connection panel, and the Knowledge Base page manages no
          connections), so a Disconnect button here would have nothing to act on. */}
    </div>
  );
}

// ── Stored dimension tile: the count is the headline, because "how much of my
// catalog does it actually have" is the only question this card is asked. The
// freshness line underneath answers the follow-up.
function StoredTile({ d, onOpen, t, showDescription = false, provider = null }) {
  const syncing = d.status === 'scraping' || d.status === 'processing';
  let value = null;
  let unit = null;
  let foot = null;

  if (d.access_mode === 'mirrored') {
    value = (d.count || 0).toLocaleString('de-CH');
    unit = t('phub_unit_synced', 'synced');
    foot = syncing
      ? (
        <span className="phub-tile-foot-sync">
          <FontAwesomeIcon icon={faSpinner} spin />{' '}
          {d.syncTotal
            ? t('phub_syncing_count', 'Syncing {{done}}/{{total}}', { done: d.syncDone || 0, total: d.syncTotal })
            : t('phub_syncing', 'Syncing…')}
        </span>
      )
      : d.last_synced
        ? t('phub_last_synced', 'Last synced {{when}}', { when: fmtWhen(d.last_synced) })
        : t('phub_never_synced', 'Not synced yet');
  } else if (d.access_mode === 'collections') {
    value = (d.count || 0).toLocaleString('de-CH');
    unit = t('phub_unit_in_use', 'in use');
    foot = d.item_count
      ? t('phub_items_mirrored', '{{count}} entries mirrored', { count: d.item_count })
      : t('phub_pick_collections', 'Choose which collections your assistant may read');
  } else {
    // store_content has no count in the payload (the listing itself resolves the
    // per-item state), so the tile states the mechanism instead of inventing a
    // number.
    value = <FontAwesomeIcon icon={faCheck} className="phub-tile-check" />;
    unit = t('phub_auto_imported', 'Auto-imported');
    foot = t('phub_store_content_foot', 'Policies, pages and posts, re-imported on demand');
  }

  return (
    <button type="button" className="phub-tile" onClick={onOpen}>
      <span className="phub-tile-top">
        <DimMark provider={provider} d={d} className="phub-tile-icon" />
        <span className="phub-tile-name">{d.label}</span>
        <span className="phub-tile-go"><FontAwesomeIcon icon={faArrowRight} /></span>
      </span>
      {showDescription && d.summary && <span className="wix-source-description">{d.summary}</span>}
      <span className="phub-tile-metric">
        <span className="phub-tile-value">{value}</span>
        <span className="phub-tile-unit">{unit}</span>
      </span>
      <span className={`phub-tile-foot${syncing ? ' phub-tile-foot-active' : ''}`}>{foot}</span>
    </button>
  );
}

// ── Live dimension row: compact by design. There is nothing to curate and no
// number to report, so a row carries it — and ten of them read as a list instead
// of a wall of equally-sized cards.
function LiveRow({ d, onOpen, t }) {
  const Tag = onOpen ? 'button' : 'div';
  return (
    <Tag
      {...(onOpen ? { type: 'button', onClick: onOpen } : {})}
      className={`phub-row${onOpen ? '' : ' phub-row-static'}`}
    >
      <span className="phub-row-icon"><FontAwesomeIcon icon={dimIcon(d.icon)} /></span>
      <span className="phub-row-main">
        <span className="phub-row-name">
          {d.label}
          {!onOpen && (
            <span className="phub-pill phub-pill-skill">{t('phub_used_by_skills', 'Used by skills')}</span>
          )}
        </span>
        {d.summary && <span className="phub-row-desc">{d.summary}</span>}
        {d.owner_only && (
          // Said out loud on the card, because listing full orders / contacts in
          // the Knowledge Base otherwise reads as "the agent hands this out".
          <span className="phub-row-note">
            <FontAwesomeIcon icon={faLock} />
            {t('phub_owner_only', 'Your view only. In chat, a visitor only ever sees their own records, and only after confirming their email.')}
          </span>
        )}
      </span>
      {onOpen && <span className="phub-row-go"><FontAwesomeIcon icon={faChevronRight} /></span>}
    </Tag>
  );
}

// ── Wix needs attention: shown instead of the data dimensions when the bot's site
// is on an old / uninstalled app ('reinstall') or consented before new permissions
// were added ('needs_scopes'). The button sends the owner to the Wix App Market
// installer for the CURRENT app — the same re-consent / reinstall flow as the
// Integrations "Connect" button (state = botId:userId → connect_bot).
function WixConnectionPrompt({ state, missing, botId, t }) {
  const needsScopes = state === 'needs_scopes';
  const go = () => {
    if (!botId) return;
    startWixConnect(botId);
  };
  return (
    <div className="phub-wix-prompt">
      <span className="phub-wix-prompt-ic"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
      <h3 className="phub-wix-prompt-title">
        {needsScopes ? t('wix_prompt_scopes_title') : t('wix_prompt_reinstall_title')}
      </h3>
      <p className="phub-wix-prompt-body">
        {needsScopes ? t('wix_prompt_scopes_body') : t('wix_prompt_reinstall_body')}
      </p>
      <button type="button" className="phub-wix-prompt-btn" onClick={go}>
        {needsScopes ? t('wix_prompt_scopes_cta') : t('wix_prompt_reinstall_cta')}
        <FontAwesomeIcon icon={faArrowRight} />
      </button>
    </div>
  );
}

// ── Shopify: connected implicitly via the App-Store install — the prompts mirror
// the Wix ones but there is nothing to "connect" from the portal (by design: the
// bind is dual-auth inside the Shopify admin, the hijack guard).
function ShopifyConnectionPrompt({ state, t }) {
  const noToken = state === 'no_token';
  return (
    <div className="phub-wix-prompt">
      <span className="phub-wix-prompt-ic"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
      <h3 className="phub-wix-prompt-title">
        {noToken
          ? t('shopify_prompt_token_title', 'Reconnect your Shopify store')
          : t('shopify_prompt_install_title', 'Connect a Shopify store')}
      </h3>
      <p className="phub-wix-prompt-body">
        {noToken
          ? t('shopify_prompt_token_body', 'The connection to your store is inactive (this happens after the app is uninstalled). Open the Ultimo Bots app in your Shopify admin once and it reconnects automatically.')
          : t('shopify_prompt_install_body', 'Install the Ultimo Bots app from the Shopify App Store on the store you want to connect, then finish the short setup there. Your store links to this account automatically.')}
      </p>
    </div>
  );
}

// ── WooCommerce: the shop comes through the Ultimo Bots WordPress plugin (1.3.0+) on the agent's site. The
// prompt says the one thing that is missing; once fixed, the plugin reports it on the next admin visit.
const WOO_PROMPTS = {
  no_site: ['woo_prompt_site_title', 'woo_prompt_site_body'],
  no_woocommerce: ['woo_prompt_woo_title', 'woo_prompt_woo_body'],
  plugin_outdated: ['woo_prompt_update_title', 'woo_prompt_update_body'],
};

function WooConnectionPrompt({ state, site, t }) {
  const [title, body] = WOO_PROMPTS[state] || WOO_PROMPTS.no_site;
  return (
    <div className="phub-wix-prompt">
      <span className="phub-wix-prompt-ic"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
      <h3 className="phub-wix-prompt-title">{t(title)}</h3>
      <p className="phub-wix-prompt-body">{t(body, { site: site || '' })}</p>
    </div>
  );
}

// ── Shopify "Policies & pages" dimension: the store-knowledge listing off the
// Admin API (policies + published Online Store pages), each row showing its
// imported state, with one import/update action through the durable scrape lane
// (existing rows are re-scraped in place — never duplicated).
function ShopifyContentPanel({ botId, onReady }) {
  const { t } = useTranslation();
  const [listing, setListing] = useState(null);
  const [err, setErr] = useState('');
  const [importing, setImporting] = useState(false);
  const [view, setView] = useState(null);   // {id, title} of the imported text being read
  const readyRef = useRef(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}apps/shopify/store_content?bot_id=${encodeURIComponent(botId)}`);
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error((j && j.error) || 'Could not load the store content.');
      setListing(j);
      setErr('');
    } catch (e) {
      setErr(e.message);
    } finally {
      if (!readyRef.current) { readyRef.current = true; onReady && onReady(); }
    }
  }, [botId, onReady]);
  useEffect(() => { load(); return () => clearTimeout(pollRef.current); }, [load]);

  const items = listing
    ? [...(listing.policies || []), ...(listing.pages || []), ...(listing.articles || [])]
    : [];
  const anyScraping = items.some((i) => i.knowledge && (i.knowledge.status === 'scraping' || i.knowledge.status === 'processing'));
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (anyScraping) pollRef.current = setTimeout(load, 4000);
    return () => clearTimeout(pollRef.current);
  }, [anyScraping, listing, load]);

  const importAll = async () => {
    if (!guardWrite() || importing || items.length === 0) return;
    setImporting(true);
    setErr('');
    try {
      const r = await fetch(`${API}apps/shopify/import_content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bot_id: botId, urls: items.map((i) => i.url) }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error((j && j.error) || 'Could not start the import.');
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setImporting(false);
    }
  };

  const chip = (item) => {
    const k = item.knowledge;
    if (!k) return <span className="phub-pill">{t('shopify_sc_not_imported', 'Not imported')}</span>;
    if (k.status === 'scraping' || k.status === 'processing') {
      return <span className="phub-pill phub-pill-sync"><FontAwesomeIcon icon={faSpinner} spin /> {t('shopify_sc_updating', 'Updating…')}</span>;
    }
    if (k.status === 'failed') return <span className="phub-pill phub-sc-failed">{t('shopify_sc_failed', 'Failed')}</span>;
    return <span className="phub-pill phub-pill-count"><FontAwesomeIcon icon={faCheck} /> {t('shopify_sc_imported', 'Imported')}</span>;
  };

  // Imported rows open the SAME content view the Knowledge Base uses
  // (KnowledgeModal on GET knowledge/{id}) — the owner sees exactly the text
  // their agent answers from, not just a size estimate.
  const section = (label, rows) => rows.length > 0 && (
    <>
      <div className="phub-sc-section">{label}</div>
      {rows.map((item) => {
        const viewable = item.knowledge && item.knowledge.status === 'completed';
        const open = () => setView({ id: item.knowledge.knowledge_id, title: item.title });
        return (
          <div
            className={`phub-sc-row${viewable ? ' phub-sc-row-view' : ''}`}
            key={item.url}
            onClick={viewable ? open : undefined}
            role={viewable ? 'button' : undefined}
            tabIndex={viewable ? 0 : undefined}
            onKeyDown={viewable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } } : undefined}
            title={viewable ? t('shopify_sc_view', 'See the imported content your agent uses') : undefined}
          >
            <div className="phub-sc-text">
              <span className="phub-sc-title">
                {item.title}
                {viewable && <FontAwesomeIcon icon={faEye} className="phub-sc-view-ic" />}
              </span>
              <span className="phub-sc-meta">{t('shopify_sc_words', 'about {{count}} words', { count: Math.max(1, Math.round((item.chars || 0) / 6)) })}</span>
            </div>
            {chip(item)}
          </div>
        );
      })}
    </>
  );

  if (!listing) {
    return err
      ? <div className="phub-sc-error">{err}</div>
      : <div className="phub-detail-spinner"><FontAwesomeIcon icon={faSpinner} spin /></div>;
  }
  return (
    <div className="phub-sc">
      {section(t('shopify_sc_policies', 'Store policies'), listing.policies || [])}
      {section(t('shopify_sc_pages', 'Store pages'), listing.pages || [])}
      {section(t('shopify_sc_articles', 'Blog posts'), listing.articles || [])}
      {items.length === 0 && (
        <div className="phub-sc-empty">{t('shopify_sc_empty', 'No policies or pages found in your Shopify settings yet.')}</div>
      )}
      {err && <div className="phub-sc-error">{err}</div>}
      {items.length > 0 && (
        <div className="phub-sc-actions">
          <button type="button" className="phub-sync-btn" onClick={importAll} disabled={importing || anyScraping}>
            <FontAwesomeIcon icon={importing || anyScraping ? faSpinner : faRotate} spin={importing || anyScraping} />{' '}
            {t('shopify_sc_import_all', 'Import & update all')}
          </button>
          <span className="phub-sc-hint">{t('shopify_sc_hint', 'Already-imported pages are updated in place, never duplicated.')}</span>
        </div>
      )}
      <KnowledgeModal open={view != null} onClose={() => setView(null)} knowledgeId={view && view.id} title={view && view.title} />
    </div>
  );
}


// ── Mirrored dimension (products): the sync + preview panel. Reuses the same
// /products/sources/{source}/sync endpoint + ProductsPreviewList as the Products
// page, driven by the WS status the parent threads in via `dim.status`.
function MirroredPanel({ provider, botId, dim, refreshHub, onReady }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [search, setSearch] = useState('');
  const safetyRef = useRef(null);
  // Automatic-sync schedule (on/off + daily/monthly), same model as the website-
  // links rescrape settings. Fetched once; the modal saves + updates it.
  const [schedule, setSchedule] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Its content (count, sync, list) renders straight from hub state — ready at once.
  useEffect(() => { if (onReady) onReady(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!botId) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}products/sync_schedule?bot_id=${encodeURIComponent(botId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setSchedule(data);
      } catch { /* leave schedule null; the row just won't render */ }
    })();
    return () => { alive = false; };
  }, [botId]);

  // Enable/disable automatic sync, mirroring the Knowledge Base → Website Links
  // rescraping toggle 1:1: flip the switch to save active on/off; a free/ineligible
  // plan opens the settings modal (its upgrade screen); enabling opens the modal so
  // the owner can pick a frequency. Owner-gated like the website toggle.
  const handleToggleSync = async (e) => {
    if (!guardWrite()) return;
    const newState = e.target.checked;
    if (schedule && schedule.plan_allowed === false) {
      setSettingsOpen(true);   // shows the upgrade screen
      return;
    }
    setToggleLoading(true);
    try {
      const res = await fetch(`${API}products/sync_schedule?bot_id=${encodeURIComponent(botId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: newState, interval: (schedule && schedule.interval) || 'daily' }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({ ...(schedule || {}), active: newState }));
        setSchedule((prev) => ({ ...(prev || {}), ...data }));
        if (newState) setSettingsOpen(true);   // let the owner choose the frequency
      }
    } catch { /* leave the toggle as-is; the next fetch reconciles */ }
    finally { setToggleLoading(false); }
  };

  const source = dim.source || provider;
  const count = dim.count || 0;
  const knowledgeId = dim.knowledge_id;
  const lastSynced = fmtWhen(dim.last_synced);
  const inProgress = dim.status === 'scraping' || dim.status === 'processing';
  const syncing = pending || inProgress;

  const clearSafety = () => { if (safetyRef.current) { clearTimeout(safetyRef.current); safetyRef.current = null; } };
  useEffect(() => clearSafety, []);

  useEffect(() => {
    if (!pending) return;
    if (dim.status === 'completed') { setPending(false); clearSafety(); setMsg({ type: 'ok', text: t('psrc_synced_done', 'Products updated.') }); }
    else if (dim.status === 'failed') { setPending(false); clearSafety(); setMsg({ type: 'err', text: t('psrc_sync_failed', 'Sync failed. Please try again.') }); }
  }, [dim.status, pending, t]);

  const runSync = async () => {
    setMsg(null); setPending(true); clearSafety();
    safetyRef.current = setTimeout(() => setPending(false), 90000);   // never stick if a WS event is missed
    let res, data;
    try {
      res = await fetch(`${API}products/sources/${encodeURIComponent(source)}/sync?bot_id=${encodeURIComponent(botId)}`, { method: 'POST' });
      data = await res.json().catch(() => ({}));
    } catch {
      setPending(false); clearSafety();
      setMsg({ type: 'err', text: t('psrc_sync_failed', 'Sync failed. Please try again.') });
      return;
    }
    if (!res.ok) {
      setPending(false); clearSafety();
      setMsg({ type: 'err', text: data.error || t('psrc_sync_failed', 'Sync failed. Please try again.') });
      return;
    }
    setMsg({ type: 'ok', text: t('psrc_sync_started', 'Sync started. Your products will update here in a moment.') });
    // Refresh so the just-created catalog (its knowledge_id + 'scraping' status) is
    // in state — that lets the WS match this sync's events on a first sync.
    if (refreshHub) await refreshHub();
  };

  return (
    <div className="phub-mirror">
      <div className="phub-mirror-row">
        <p className="phub-mirror-line">
          {syncing
            ? (dim.syncTotal
                ? t('psrc_wix_syncing', 'Syncing your catalog… {{done}}/{{total}} products', { done: dim.syncDone || 0, total: dim.syncTotal })
                : t('psrc_wix_syncing_plain', 'Syncing your catalog…'))
            : (count > 0
                ? (lastSynced
                    ? <span className="phub-mirror-when">{t('psrc_wix_last', 'Last synced {{when}}', { when: lastSynced })}</span>
                    : null)
                : t('psrc_wix_count_zero', 'No products mirrored yet. Make sure your products are published (visible), then sync.'))}
        </p>
        <button type="button" className="phub-sync-btn" onClick={runSync} disabled={syncing}>
          <FontAwesomeIcon icon={syncing ? faSpinner : faRotate} spin={syncing} />
          {syncing
            ? (dim.syncTotal
                ? t('phub_syncing_count', 'Syncing {{done}}/{{total}}', { done: dim.syncDone || 0, total: dim.syncTotal })
                : t('psrc_syncing', 'Syncing…'))
            : (count > 0 ? t('psrc_resync', 'Sync now') : t('psrc_sync_now', 'Sync products'))}
        </button>
      </div>

      {msg && <p className={`psrc-msg ${msg.type === 'ok' ? 'is-ok' : 'is-err'}`}>{msg.text}</p>}

      {schedule && (
        // 1:1 with Knowledge Base → Website Links "Activate automatic rescraping":
        // a purple switch + label, plus a sliders button (shown when active) that
        // opens the settings modal. Reuses the .rescraping-* classes from
        // knowledgeOverview.css (imported above) for identical styling.
        <div className="rescraping-activate-settings-container">
          {toggleLoading ? (
            <div className="rescraping-toggle-loading">
              <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', marginRight: '8px' }} />
              <span>{t('psync_activate', 'Activate automatic sync')}</span>
            </div>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={!!(schedule.active && schedule.plan_allowed !== false)}
                  onChange={handleToggleSync}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                    '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                  }}
                />
              }
              className="rescraping-toggle"
              label={t('psync_activate', 'Activate automatic sync')}
            />
          )}
          {schedule.active && schedule.plan_allowed !== false && (
            <button
              className="rescraping-settings-button"
              onClick={() => setSettingsOpen(true)}
            >
              <FontAwesomeIcon icon={faSliders} />
            </button>
          )}
        </div>
      )}

      <ProductSyncSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        botId={botId}
        schedule={schedule}
        onSaved={(s) => setSchedule((prev) => ({ ...(prev || {}), ...s }))}
      />

      {knowledgeId && count > 0 && (
        <div className="phub-preview">
          <div className="phub-preview-head">
            <span>{t('psrc_products_in_catalog', 'Products your AI agent can show and recommend')} ({count})</span>
            <div className="knowledge-search-container">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
              <input
                type="text"
                className="knowledge-search-input"
                placeholder={t('search') || 'Search...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button type="button" className="knowledge-search-clear" onClick={() => setSearch('')} aria-label={t('clear_search')}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>
          </div>
          <ProductsPreviewList botId={botId} knowledgeId={knowledgeId} search={search} />
        </div>
      )}
    </div>
  );
}

// ── Live dimension: fetch the preview (runs the dimension's preview_action) and
// render the structured list the agent itself sees. Nothing is stored.
function LivePreviewPanel({ provider, botId, dim, onReady }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  // Tell the parent to release the held height once the fetch resolves (so it
  // animates to the real size with the data already in place).
  useEffect(() => { if (!state.loading && onReady) onReady(); }, [state.loading]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    (async () => {
      try {
        const res = await fetch(
          `${API}apps/${encodeURIComponent(provider)}/dimensions/${encodeURIComponent(dim.key)}/preview?bot_id=${encodeURIComponent(botId)}`,
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || data.error) { setState({ loading: false, data: null, error: data.error || 'error' }); return; }
        setState({ loading: false, data, error: null });
      } catch {
        if (!cancelled) setState({ loading: false, data: null, error: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [provider, dim.key, botId]);

  if (state.loading) {
    return (
      <div className="phub-preview-loading">
        <FontAwesomeIcon icon={faSpinner} spin /> {t('phub_loading_live', 'Fetching live from {{label}}…', { label: dim.label })}
      </div>
    );
  }
  if (state.error) {
    return <p className="phub-preview-empty">{t('phub_preview_error', 'Could not load this live data right now. Please try again.')}</p>;
  }
  if (state.data && state.data.unavailable) {
    return (
      <p className="phub-preview-empty">
        {t('phub_preview_unavailable', 'This is not set up on your {{label}} site.', { label: dim.label })}
      </p>
    );
  }
  const items = (state.data && state.data.items) || [];
  const fields = (state.data && state.data.fields && state.data.fields.length)
    ? state.data.fields
    : (items[0] ? Object.keys(items[0]) : []);
  if (items.length === 0) {
    return <p className="phub-preview-empty">{(state.data && state.data.empty) || t('phub_preview_none', 'Nothing here yet.')}</p>;
  }
  return (
    <div className="phub-live">
      <div className="phub-live-banner">
        <FontAwesomeIcon icon={faBolt} />
        {provider === 'wix' ? t('wix_live_preview_hint') : t('phub_live_banner', 'Live from {{label}}. This is exactly what your assistant sees, fetched on demand and not stored.', { label: dim.label })}
      </div>
      <div className="phub-table-wrap">
        <table className="phub-table">
          <thead>
            <tr>{fields.map((f) => <th key={f}>{f.replace(/_/g, ' ')}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>{fields.map((f) => <td key={f}>{fmtCell(it[f])}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="phub-live-count">{t('phub_live_count', '{{count}} shown', { count: items.length })}</p>
    </div>
  );
}

// ── Collections dimension (Wix CMS / Site Content): discover the site's custom
// collections and let the owner toggle which ones the assistant uses. The list is
// LOCAL-AUTHORITATIVE: an initial discovery seeds it, then a toggle updates ONLY its
// own row and per-collection sync status arrives over the SAME {knowledge_id, status}
// WebSocket the rest of the hub uses (matched by each enabled collection's
// knowledge_id). We never re-fetch the whole list on a toggle, so a stale in-flight
// response can't clobber a row the user just flipped (the on→off→on flicker).
function CollectionsPanel({ botId, dim, onReady }) {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});            // collection_id -> toggle POST in flight
  // One auto-sync toggle for ALL site content: when on, every collection the owner
  // marked "Used" is re-synced on a schedule (the CMS twin of the Products row).
  const [schedule, setSchedule] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const readyRef = useRef(false);
  // onReady is a fresh closure on every PARENT render (and the parent re-renders on
  // its own WebSocket), so keep it in a ref and NEVER in load's deps — otherwise
  // load changes identity, the initial-load effect re-fires a full reload, and a
  // stale response clobbers an in-flight toggle (on → off → on).
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  // load({merge}) — full replace on first load; a MERGE only refreshes
  // synced_count / last_synced / item_count by collection_id and never touches the
  // local enabled / status / knowledge_id, so it can't fight an in-flight toggle.
  const load = useCallback(async ({ merge = false } = {}) => {
    try {
      const res = await fetch(`${API}apps/wix/cms/collections?bot_id=${encodeURIComponent(botId)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (!merge) setError(data.error || 'error');
        return;
      }
      const server = data.collections || [];
      setError(null);
      if (merge) {
        setCollections((prev) => prev.map((c) => {
          const s = server.find((x) => x.collection_id === c.collection_id);
          return s ? { ...c, synced_count: s.synced_count, last_synced: s.last_synced, item_count: s.item_count } : c;
        }));
      } else {
        setCollections(server);
      }
    } catch {
      if (!merge) setError('error');
    } finally {
      if (!merge) {
        setLoading(false);
        if (!readyRef.current) { readyRef.current = true; const cb = onReadyRef.current; if (cb) cb(); }   // release the parent's held height
      }
    }
  }, [botId]);   // NOT onReady (kept in a ref) — load must stay stable so the effect below runs once

  useEffect(() => { load(); }, [load]);

  // The bot's site-content auto-sync schedule (on/off + daily/monthly). Fetched once;
  // the modal saves + updates it. Left null on error so the row simply won't render.
  useEffect(() => {
    if (!botId) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}apps/wix/cms/sync_schedule?bot_id=${encodeURIComponent(botId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setSchedule(data);
      } catch { /* leave schedule null */ }
    })();
    return () => { alive = false; };
  }, [botId]);

  // Refs so the WebSocket handler reads the latest load/collections without
  // re-subscribing (and reconnecting) on every render.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  const collectionsRef = useRef(collections);
  useEffect(() => { collectionsRef.current = collections; }, [collections]);

  // Live per-collection sync status over the SAME knowledge-status WebSocket the
  // hub + knowledge base use — matched by each enabled collection's knowledge_id.
  // No polling.
  useEffect(() => {
    if (!botId) return undefined;
    let socket;
    let reconnectTimer = null;
    let alive = true;
    const connect = () => {
      try { socket = new WebSocket(process.env.REACT_APP_API_WEBSOCKET_URL); } catch { return; }
      socket.onopen = () => {
        try {
          socket.send(JSON.stringify({ type: 'init', user_id: localStorage.getItem('user_id'), token: localStorage.getItem('token') }));
        } catch { /* socket closed mid-handshake */ }
      };
      socket.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        if (!msg || !msg.knowledge_id || !msg.status) return;
        const match = collectionsRef.current.find((c) => c.knowledge_id && c.knowledge_id === msg.knowledge_id);
        if (!match) return;
        setCollections((prev) => prev.map((c) => (c.knowledge_id === msg.knowledge_id ? { ...c, status: msg.status } : c)));
        // Terminal status → merge-refresh the synced count/time for that row.
        if (msg.status === 'completed' || msg.status === 'failed') loadRef.current({ merge: true });
      };
      socket.onclose = (evt) => {
        if (!alive) return;
        if (evt && evt.code === 4401) return;        // auth failure: no reconnect loop
        reconnectTimer = setTimeout(connect, 5000);
      };
    };
    connect();
    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) { socket.onclose = null; try { socket.close(); } catch { /* already closed */ } }
    };
  }, [botId]);

  const toggle = useCallback(async (c) => {
    if (busy[c.collection_id]) return;               // guard against double-clicks mid-flight
    const next = !c.enabled;
    setBusy((b) => ({ ...b, [c.collection_id]: true }));
    // Optimistic: update ONLY this row. Enable → 'processing' (the WS drives it to
    // completed); disable → clear its synced state.
    setCollections((prev) => prev.map((x) => (x.collection_id === c.collection_id
      ? (next
          ? { ...x, enabled: true, status: 'processing' }
          : { ...x, enabled: false, status: 'idle', synced_count: 0, last_synced: null, knowledge_id: null })
      : x)));
    try {
      const res = await fetch(`${API}apps/wix/cms/collections/${encodeURIComponent(c.collection_id)}?bot_id=${encodeURIComponent(botId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'toggle_failed');
      // Bind the new knowledge_id so the WS can match this row's sync events.
      if (next && data.knowledge_id) {
        setCollections((prev) => prev.map((x) => (x.collection_id === c.collection_id ? { ...x, knowledge_id: data.knowledge_id } : x)));
      }
    } catch {
      // Revert this row to exactly what it was before the optimistic flip.
      setCollections((prev) => prev.map((x) => (x.collection_id === c.collection_id
        ? { ...x, enabled: c.enabled, status: c.status, synced_count: c.synced_count, last_synced: c.last_synced, knowledge_id: c.knowledge_id }
        : x)));
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[c.collection_id]; return n; });
    }
  }, [botId, busy]);

  // Re-sync ONE already-enabled collection (the per-row "Sync now"). Optimistically
  // flips the row to 'processing'; the WS drives it back to completed.
  const syncCollection = useCallback(async (c) => {
    if (busy[c.collection_id] || !c.enabled) return;
    setBusy((b) => ({ ...b, [c.collection_id]: true }));
    setCollections((prev) => prev.map((x) => (x.collection_id === c.collection_id ? { ...x, status: 'processing' } : x)));
    try {
      const res = await fetch(`${API}apps/wix/cms/collections/${encodeURIComponent(c.collection_id)}/sync?bot_id=${encodeURIComponent(botId)}`, { method: 'POST' });
      if (!res.ok) throw new Error('sync_failed');
    } catch {
      setCollections((prev) => prev.map((x) => (x.collection_id === c.collection_id ? { ...x, status: c.status } : x)));
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[c.collection_id]; return n; });
    }
  }, [botId, busy]);

  // Enable/disable automatic site-content sync (ONE toggle for all "Used"
  // collections). Mirrors the Products row: a free/ineligible plan opens the upgrade
  // screen; enabling opens the settings modal so the owner can pick a frequency.
  const handleToggleSync = async (e) => {
    if (!guardWrite()) return;
    const newState = e.target.checked;
    if (schedule && schedule.plan_allowed === false) { setSettingsOpen(true); return; }
    setToggleLoading(true);
    try {
      const res = await fetch(`${API}apps/wix/cms/sync_schedule?bot_id=${encodeURIComponent(botId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: newState, interval: (schedule && schedule.interval) || 'daily' }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({ ...(schedule || {}), active: newState }));
        setSchedule((prev) => ({ ...(prev || {}), ...data }));
        if (newState) setSettingsOpen(true);   // let the owner choose the frequency
      }
    } catch { /* leave the toggle as-is; the next fetch reconciles */ }
    finally { setToggleLoading(false); }
  };

  if (loading) {
    return (
      <div className="phub-preview-loading">
        <FontAwesomeIcon icon={faSpinner} spin /> {t('phub_cms_loading', 'Reading your site content…')}
      </div>
    );
  }
  if (error) {
    return (
      <p className="phub-preview-empty">
        {t('phub_cms_error', "Could not read this site's content collections. The site may need its code editor enabled, or the Wix app re-authorized.")}
      </p>
    );
  }
  if (!collections.length) {
    return <p className="phub-preview-empty">{t('phub_cms_empty', 'No public content collections were found on this site.')}</p>;
  }

  const hasEnabled = collections.some((c) => c.enabled);

  return (
    <div className="phub-cms">
      {schedule && hasEnabled && (
        // One auto-sync control for ALL "Used" collections, ABOVE the list (same place
        // as Products). The exact row Products uses (.rescraping-* from knowledgeOverview.css).
        <div className="rescraping-activate-settings-container phub-cms-autosync">
          {toggleLoading ? (
            <div className="rescraping-toggle-loading">
              <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', marginRight: '8px' }} />
              <span>{t('cmssync_activate', 'Activate automatic sync')}</span>
            </div>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={!!(schedule.active && schedule.plan_allowed !== false)}
                  onChange={handleToggleSync}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                    '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                  }}
                />
              }
              className="rescraping-toggle"
              label={t('cmssync_activate', 'Activate automatic sync')}
            />
          )}
          {schedule.active && schedule.plan_allowed !== false && (
            <button className="rescraping-settings-button" onClick={() => setSettingsOpen(true)}>
              <FontAwesomeIcon icon={faSliders} />
            </button>
          )}
        </div>
      )}

      <div className="phub-cms-list">
        {collections.map((c) => (
          <CmsRow key={c.collection_id} c={c} botId={botId} busy={!!busy[c.collection_id]} onToggle={() => toggle(c)} onSync={() => syncCollection(c)} t={t} />
        ))}
      </div>

      <ProductSyncSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        botId={botId}
        schedule={schedule}
        endpoint="apps/wix/cms/sync_schedule"
        title={t('cmssync_title', 'Automatic site content sync')}
        subtitle={t('cmssync_subtitle', 'Keep your site content up to date automatically. We re-check every collection you use on the schedule below.')}
        upgradeTitle={t('cmssync_upgrade_title', 'Keep your site content up to date automatically')}
        upgradeDescription={t('cmssync_upgrade_description', 'Automatic sync re-checks the collections you use on a schedule so your assistant always has the latest content. Available on every paid plan.')}
        onSaved={(s) => setSchedule((prev) => ({ ...(prev || {}), ...s }))}
      />
    </div>
  );
}

// One collection row, EXPANDABLE: the whole header toggles a smooth collapse that
// holds the searchable, paginated item list. Clicking anywhere on the header toggles
// it EXCEPT the action buttons (Sync now / Used), which stop the click. A chevron
// after the toggle rotates on expand.
function CmsRow({ c, botId, busy, onToggle, onSync, t }) {
  const [shown, setShown] = useState(false);
  const [search, setSearch] = useState('');
  const syncing = c.status === 'processing' || c.status === 'scraping';
  const failed = c.status === 'failed';
  const fields = (c.fields || []).map((f) => f.key).filter(Boolean).slice(0, 6).join(', ');
  const stop = (e) => e.stopPropagation();   // keep the action buttons from toggling the row

  return (
    <div className="phub-cms-entry">
      <div
        className={`phub-cms-row${c.enabled ? ' is-clickable' : ''}`}
        role={c.enabled ? 'button' : undefined}
        tabIndex={c.enabled ? 0 : undefined}
        aria-expanded={c.enabled ? shown : undefined}
        onClick={c.enabled ? () => setShown((s) => !s) : undefined}
        onKeyDown={c.enabled ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShown((s) => !s); } } : undefined}
      >
        <div className="phub-cms-row-body">
          <div className="phub-cms-row-main">
            <span className="phub-cms-name">{c.display_name || c.collection_id}</span>
            <span className="phub-cms-badge">{t('phub_cms_public', 'Public')}</span>
          </div>
          <div className="phub-cms-sub">
            {t('phub_cms_items', '{{count}} items', { count: c.item_count || 0 })}
            {fields && <span> · {fields}</span>}
            {c.enabled && !syncing && !failed && (
              <span className="phub-cms-synced"> · {t('phub_cms_synced', '{{count}} synced', { count: c.synced_count || 0 })}</span>
            )}
            {failed && <span className="phub-cms-failed"> · {t('phub_cms_failed_short', 'Sync failed, toggle to retry')}</span>}
          </div>
        </div>
        <div className="phub-cms-row-actions" onClick={stop}>
          {c.enabled && (
            <button type="button" className="phub-sync-btn" onClick={onSync} disabled={busy || syncing}>
              <FontAwesomeIcon icon={(busy || syncing) ? faSpinner : faRotate} spin={busy || syncing} />
              {t('phub_cms_sync_now', 'Sync now')}
            </button>
          )}
          <button
            type="button"
            className={`phub-cms-toggle${c.enabled ? ' is-on' : ''}`}
            onClick={onToggle}
            disabled={busy || syncing}
          >
            {syncing ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> {t('phub_cms_syncing', 'Syncing…')}</>
            ) : busy ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <><FontAwesomeIcon icon={c.enabled ? faToggleOn : faToggleOff} /> {c.enabled ? t('phub_cms_used', 'Used') : t('phub_cms_not_used', 'Not used')}</>
            )}
          </button>
        </div>
        {c.enabled && (
          <FontAwesomeIcon icon={faChevronDown} className={`phub-cms-chevron${shown ? ' is-open' : ''}`} />
        )}
      </div>
      {c.enabled && (
        <div className={`phub-cms-collapse${shown ? ' is-open' : ''}`}>
          <div className="phub-cms-collapse-inner">
            <div className="phub-preview phub-cms-preview-wrap">
              <div className="phub-preview-head">
                <span>{t('phub_cms_items_in', 'Items in {{name}}', { name: c.display_name || c.collection_id })}</span>
                <div className="knowledge-search-container">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                  <input
                    type="text"
                    className="knowledge-search-input"
                    placeholder={t('search') || 'Search...'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button type="button" className="knowledge-search-clear" onClick={() => setSearch('')} aria-label={t('clear_search') || 'Clear'}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
              </div>
              <CmsPreviewList active={shown} botId={botId} collectionId={c.collection_id} search={search} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The paginated item list of one CMS collection (10/page) — mirrors
// ProductsPreviewList. `search` filters title/body server-side across all pages.
function CmsPreviewList({ botId, collectionId, search = '', active = true }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { setPage(1); }, [collectionId, search]);

  const fetchPage = useCallback(async () => {
    if (!collectionId || !botId || !active) return;   // only fetch once the row is expanded
    setLoading(true);
    setError(null);
    try {
      const q = search && search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
      const res = await fetch(
        `${API}apps/wix/cms/collections/${encodeURIComponent(collectionId)}/items?bot_id=${encodeURIComponent(botId)}&page=${page}${q}`,
      );
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      setItems(data.items || []);
      setPages(data.pages || 1);
    } catch {
      setError(t('phub_cms_items_error', 'Could not load items.'));
    } finally {
      setLoading(false);
    }
  }, [collectionId, botId, page, search, t, active]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 140 }}>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#5616ea" />
      </Box>
    );
  }
  if (error) {
    return <p style={{ color: '#d23f3f', padding: '8px 2px' }}>{error}</p>;
  }
  if (!items.length) {
    return (
      <p style={{ color: '#8a8a96', padding: '14px 2px', fontSize: 13.5 }}>
        {search ? t('phub_cms_no_results', 'No items match your search.') : t('phub_cms_no_items', 'No items yet.')}
      </p>
    );
  }

  return (
    <>
      <div className="pp-list">
        {items.map((it, i) => (
          <div className="pp-row" key={it.external_id || i}>
            <div className="pp-thumb"><FontAwesomeIcon icon={faDatabase} /></div>
            <div className="pp-main">
              <div className="pp-title-row">
                <span className="pp-title">{it.title || t('phub_cms_untitled', 'Untitled')}</span>
              </div>
              {it.body && <span className="pp-specs">{it.body}</span>}
            </div>
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="pp-pager">
          <button className="pp-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="pp-page-info">{t('pp_page', 'Page')} {page} / {pages}</span>
          <button className="pp-page-btn" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </>
  );
}

